import { createTheme } from "@mui/material/styles"
import { getSavedLanguage } from "@/i18n"
import { deDE, enUS, esES, frFR, itIT, jaJP, svSE, arSA } from "@mui/material/locale"
import {
    deDE as dataGridDeDE,
    enUS as dataGridEnUS,
    esES as dataGridEsES,
    frFR as dataGridFrFR,
    itIT as dataGridItIT,
    jaJP as dataGridJaJP,
    svSE as dataGridSvSE,
    arSD as dataGridArSA,
} from "@mui/x-data-grid/locales"
import { C, radius, shadow, transition } from "@/theme/newDesignTokens"
import { APP_FONT_FAMILY } from "./typography"

const baseColors = [
    "#7A80BD",
    "#B1C1FB",
    "#7D7F9A",
    "#F8B77D",
    "#F39C8C",
    "#5EDC99",
    "#6AAFE3",
    "#B77BC6",
    "#4ED9B3",
    "#FFE6B3",
    "#FFB27D",
    "#8C9DB3",
]

export const citationColors = Array(5).fill(baseColors).flat()

function CustomScrollbar(options?: { track: string; thumb: string; active: string }): {
    scrollbarColor: string
    "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
        width: string
        height: string
        backgroundColor: string
    }
    "&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track": {
        backgroundColor: string
    }
    "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
        borderRadius: string
        backgroundColor: string
        border: string
    }
    "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover": {
        backgroundColor: string
    }
} {
    const { track = "#f5f5f5", thumb = "#bdbdbd", active = "#9e9e9e" } = options || {}

    return {
        scrollbarColor: `${thumb} / ${track}`,
        "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
            width: "12px",
            height: "12px",
            backgroundColor: track,
        },
        "&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track": {
            // a bit transparent to see the thumb color
            backgroundColor: `${track}99`,
        },
        "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
            borderRadius: "6px",
            backgroundColor: thumb,
            border: "3px solid #f5f5f5",
        },
        "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover": {
            backgroundColor: active,
        },
    }
}

const lang = getSavedLanguage()

const localeSettings = {
    de: { ...deDE, ...dataGridDeDE },
    fr: { ...frFR, ...dataGridFrFR },
    es: { ...esES, ...dataGridEsES },
    it: { ...itIT, ...dataGridItIT },
    sv: { ...svSE, ...dataGridSvSE },
    ja: { ...jaJP, ...dataGridJaJP },
    en: { ...enUS, ...dataGridEnUS },
    ar: { ...arSA, ...dataGridArSA },
    uk: { ...enUS, ...dataGridEnUS },
}[lang] || { ...enUS, ...dataGridEnUS }

const theme = createTheme(
    {
        palette: {
            staticColors: {
                bodyColor: "#E4EFFF",
                headerColor: "#fff",
                showreelColor: "#4e7cbf",
                menuColor: "#003366",
                questionBoxColor: "#fff",
                questionDefaultBoxColor: "#e6ccff",
                userChatBoxColor: "#bed7fd",
                footerColor: "#122e52",
                exampleBoxBackgroundLight: "#D2EAFF",
                exampleBoxBackgroundDark: "#4F7CBF",
                listHeader: "#4F7CBF",
                listElementLight: "#FFF",
                listElementDark: "#D2E9FF",
            },
            primary: {
                main: "#4e7cbf",
            },
            secondary: {
                main: "#cecece",
            },
            background: {
                default: "#f1f1f1",
                paper: "#fff",
            },
        },
        typography: {
            fontFamily: APP_FONT_FAMILY,
            fontSize: 14,

            h1: {
                fontSize: 26,
                lineHeight: 1.2,
            },
            h2: {
                fontSize: 22,
                lineHeight: 1.2,
            },
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: themeParam => ({
                    ":root": {
                        "--aidocr-navy": C.navy,
                        "--aidocr-navy-dark": C.navyDark,
                        "--aidocr-navy-hover": C.navyHover,
                        "--aidocr-off-white": C.offWhite,
                        "--aidocr-light-gray": C.lightGray,
                        "--aidocr-med-gray": C.medGray,
                        "--aidocr-white": C.white,
                        "--aidocr-black": C.black,
                        "--aidocr-navy-tint": `${C.navy}14`,
                        "--aidocr-transition-fast": transition.fast,
                        "--aidocr-transition-normal": transition.normal,
                        "--aidocr-shadow-sm": shadow.sm,
                        "--aidocr-shadow-md": shadow.md,
                    },
                    body: CustomScrollbar(),
                }),
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    label: {
                        fontFamily: APP_FONT_FAMILY,
                        fontSize: "14px",
                        textTransform: "none",
                        // paddingTop: "6px",
                        // paddingBottom: "5px",
                    },
                },
            },
            MuiButtonBase: {
                styleOverrides: {
                    root: {
                        transition: transition.fast,
                        "&:not(.Mui-disabled):hover": {
                            filter: "brightness(0.97)",
                        },
                    },
                },
            },
            MuiButton: {
                defaultProps: {
                    disableElevation: true,
                },
                styleOverrides: {
                    root: {
                        fontFamily: APP_FONT_FAMILY,
                        fontSize: 12,
                        fontWeight: 600,
                        lineHeight: "normal",
                        textTransform: "none",
                        borderRadius: radius.md,
                        padding: "9px 16px",
                        transition: transition.fast,
                        "&.Mui-disabled": { opacity: 0.48 },
                    },
                    containedPrimary: {
                        backgroundColor: C.navy,
                        color: C.white,
                        "&:hover": { backgroundColor: C.navyHover },
                        "&.Mui-disabled": { backgroundColor: C.navy, color: C.white },
                    },
                    outlinedPrimary: {
                        borderColor: C.periwinkle,
                        color: C.navy,
                        "&:hover": { borderColor: C.navy, backgroundColor: `${C.navy}08` },
                    },
                    outlinedError: {
                        borderColor: C.coral,
                        color: C.coral,
                        "&:hover": { borderColor: C.coral, backgroundColor: `${C.coral}08` },
                    },
                    containedError: {
                        backgroundColor: C.coral,
                        color: C.white,
                        "&:hover": { backgroundColor: C.coral },
                    },
                },
            },
            MuiTooltip: {
                defaultProps: {
                    enterDelay: 400,
                    enterNextDelay: 400,
                    enterTouchDelay: 400,
                },
                styleOverrides: {
                    tooltip: {
                        backgroundColor: C.white,
                        color: C.black,
                        border: `1px solid ${C.lightGray}`,
                        borderRadius: radius.md,
                        boxShadow: shadow.md,
                        padding: "8px 12px",
                        fontSize: 12,
                        lineHeight: 1.5,
                        maxWidth: "min(500px, calc(100vw - 32px))",
                        overflowWrap: "anywhere",
                    },
                    arrow: {
                        color: C.white,
                        "&::before": { border: `1px solid ${C.lightGray}` },
                    },
                },
            },
            // @ts-ignore
            MuiDataGrid: {
                styleOverrides: {
                    toolbarContainer: {
                        borderRadius: "4px",
                        borderBottomRightRadius: "0px",
                        borderBottomLeftRadius: "0px",
                    },
                    root: {
                        borderRadius: "4px",
                        borderTopRightRadius: "0px",
                        borderTopLeftRadius: "0px",
                    },
                    columnHeader: {
                        borderRadius: "0px !important",
                    },
                },
            },
            // @ts-ignore
            MuiLink: {
                styleOverrides: {
                    root: {
                        "&[disabled]": {
                            color: "inherit",
                            pointerEvents: "none",
                        },
                    },
                },
            },
        },
    },
    localeSettings
)

export default theme
